import React from 'react';
import Button from '../../components/Button';
import { parseTranscript } from './utils/transcript';

const DECISION_STYLES = {
  select: { ring: 'ring-emerald-400', text: 'text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-50 dark:bg-emerald-900/20', bar: 'bg-emerald-500' },
  maybe: { ring: 'ring-amber-400', text: 'text-amber-700 dark:text-amber-300', bg: 'bg-amber-50 dark:bg-amber-900/20', bar: 'bg-amber-500' },
  reject: { ring: 'ring-red-400', text: 'text-red-700 dark:text-red-300', bg: 'bg-red-50 dark:bg-red-900/20', bar: 'bg-red-500' },
};

const Scorecard = ({ evaluation, evaluating }) => {
  if (evaluating && !evaluation) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-5 text-sm text-slate-500 dark:text-slate-300 text-center">
        Scoring the interview…
      </div>
    );
  }
  if (!evaluation) return null;
  if (!evaluation.available) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-4 text-sm text-slate-500 dark:text-slate-300">
        Scorecard isn’t available for this interview{evaluation.error ? ` — ${evaluation.error}` : ''}.
      </div>
    );
  }

  const d = DECISION_STYLES[evaluation.decision] || DECISION_STYLES.maybe;
  return (
    <div className="space-y-5">
      {/* Headline: overall score + decision */}
      <div className={`flex items-center gap-4 rounded-xl p-4 ${d.bg}`}>
        <div className={`w-20 h-20 rounded-full bg-white dark:bg-gray-900 ring-4 ${d.ring} flex flex-col items-center justify-center shrink-0`}>
          <span className="text-2xl font-bold text-slate-800 dark:text-white leading-none">{evaluation.overall_score == null ? '--' : evaluation.overall_score}</span>
          <span className="text-[10px] text-slate-400">/ {evaluation.overall_max}</span>
        </div>
        <div>
          <div className={`text-lg font-semibold ${d.text}`}>{evaluation.decision_label}</div>
          {evaluation.summary && (
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-0.5">{evaluation.summary}</p>
          )}
        </div>
      </div>

      {/* Per-criterion scores */}
      <div className="space-y-3">
        {(evaluation.criteria || []).map((c) => (
          <div key={c.key}>
            <div className="flex items-baseline justify-between text-sm">
              <span className="font-medium text-slate-700 dark:text-slate-200">
                {c.label} <span className="text-xs text-slate-400">· {c.weight}%</span>
              </span>
              <span className="text-slate-500 dark:text-slate-400 tabular-nums">{c.score}/{c.max}</span>
            </div>
            <div className="h-1.5 mt-1 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
              <div className={`h-full ${d.bar}`} style={{ width: `${(c.score / c.max) * 100}%` }} />
            </div>
            {c.reason && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{c.reason}</p>}
          </div>
        ))}
      </div>

      {/* Reasons */}
      <div className="grid sm:grid-cols-2 gap-4">
        <ReasonList title="Reasons to select" items={evaluation.selection_reasons} tone="emerald" />
        <ReasonList title="Reasons to reject" items={evaluation.rejection_reasons} tone="red" />
      </div>

      {evaluation.practical_task?.available && (
        <PracticalTaskPanel practicalTask={evaluation.practical_task} />
      )}

      {evaluation.coverage && <CoveragePanel coverage={evaluation.coverage} />}
    </div>
  );
};

const PracticalTaskPanel = ({ practicalTask }) => {
  const task = practicalTask.task || {};
  const ev = practicalTask.evaluation || {};
  const rubric = ev.rubric_results || [];
  const evidence = ev.evidence_observed || [];
  const missing = ev.missing_evidence || [];
  const concerns = ev.concerns || [];
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div>
          <div className="font-semibold text-slate-700 dark:text-slate-200">Practical task</div>
          {task.title && <div className="text-xs text-slate-500 dark:text-slate-400">{task.title}</div>}
        </div>
        {ev.score != null && (
          <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
            {ev.score}/{ev.max_score || 10}
            {ev.confidence != null ? ` · ${Math.round(Number(ev.confidence || 0) * 100)}%` : ''}
          </span>
        )}
      </div>
      {ev.summary && <p className="text-slate-600 dark:text-slate-300">{ev.summary}</p>}
      {practicalTask.status && (
        <p className="mt-1 text-xs text-slate-400">
          Status: {practicalTask.status}
          {practicalTask.time_spent_seconds != null ? ` · ${Math.ceil(practicalTask.time_spent_seconds / 60)} min` : ''}
        </p>
      )}
      {rubric.length > 0 && (
        <div className="mt-3 space-y-2">
          {rubric.map((r, i) => (
            <div key={i} className="rounded-md bg-gray-50 p-2 dark:bg-gray-800">
              <div className="flex justify-between gap-3">
                <span className="font-medium text-slate-700 dark:text-slate-200">{r.criterion}</span>
                <span className="text-slate-500 dark:text-slate-400">{r.score}/{r.max_score}</span>
              </div>
              {r.reason && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{r.reason}</p>}
            </div>
          ))}
        </div>
      )}
      {evidence.length > 0 && (
        <div className="mt-3 rounded-md bg-emerald-50 p-2 dark:bg-emerald-900/20">
          <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">Evidence observed</p>
          <ul className="mt-1 list-disc pl-4 text-xs text-emerald-900 dark:text-emerald-100">
            {evidence.map((item, i) => <li key={`task-evidence-${i}`}>{item}</li>)}
          </ul>
        </div>
      )}
      {missing.length > 0 && (
        <div className="mt-3 rounded-md bg-amber-50 p-2 dark:bg-amber-900/20">
          <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">Missing evidence</p>
          <ul className="mt-1 list-disc pl-4 text-xs text-amber-900 dark:text-amber-100">
            {missing.map((item, i) => <li key={`task-missing-${i}`}>{item}</li>)}
          </ul>
        </div>
      )}
      {concerns.length > 0 && (
        <div className="mt-3 rounded-md bg-rose-50 p-2 dark:bg-rose-900/20">
          <p className="text-xs font-semibold text-rose-700 dark:text-rose-300">
            Concerns{ev.human_review_required ? ' · human review required' : ''}
          </p>
          <ul className="mt-1 list-disc pl-4 text-xs text-rose-900 dark:text-rose-100">
            {concerns.map((item, i) => <li key={`task-concern-${i}`}>{item}</li>)}
          </ul>
        </div>
      )}
      {practicalTask.answer_text && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs font-medium text-slate-500 dark:text-slate-400">View task answer</summary>
          <p className="mt-2 whitespace-pre-wrap rounded-md bg-gray-50 p-2 text-xs text-slate-600 dark:bg-gray-800 dark:text-slate-300">
            {practicalTask.answer_text}
          </p>
        </details>
      )}
    </div>
  );
};

// Internal coverage summary (recruiter-only): were all questions asked, and all required topics covered?
const CoveragePanel = ({ coverage }) => {
  const cov = coverage || {};
  const required = (cov.required_skills || []).length;
  const covered = (cov.covered_skills || []).length;
  const missing = cov.missing_skills || [];
  const Tick = ({ ok }) => (
    <span className={ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}>
      {ok ? '✓' : '✗'}
    </span>
  );
  // Unrestricted mode plans a coverage-driven SUPERSET and wraps once the role is covered, so "unasked
  // plan items" is expected — don't show it as a shortfall. Other modes keep the planned-vs-asked check.
  const unaskedCats = cov.unasked_categories || [];
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm">
      <div className="mb-2 font-semibold text-slate-700 dark:text-slate-200">Coverage</div>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-slate-600 dark:text-slate-300">
        {cov.unrestricted ? (
          <span>Questions asked
            {' '}<span className="text-xs text-slate-400">({cov.main_questions_asked})</span>
          </span>
        ) : (
          <span><Tick ok={!!cov.all_questions_asked} /> All planned questions asked
            {' '}<span className="text-xs text-slate-400">({cov.main_questions_asked}/{cov.planned_questions})</span>
          </span>
        )}
        {required > 0 && (
          <span><Tick ok={!!cov.all_topics_covered} /> Required topics covered
            {' '}<span className="text-xs text-slate-400">({covered}/{required})</span>
          </span>
        )}
        {cov.end_reason && <span className="text-xs text-slate-400">end: {cov.end_reason}</span>}
      </div>
      {missing.length > 0 && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
          Not covered: {missing.join(', ')}
        </p>
      )}
      {/* Only show "Unasked" for fixed-count interviews (where it's a real gap); hide for unrestricted. */}
      {!cov.unrestricted && unaskedCats.length > 0 && (
        <p className="mt-1 text-xs text-slate-400">
          Unasked: {unaskedCats.map((u) => `${u.label}${u.count > 1 ? ` ×${u.count}` : ''}`).join(', ')}
        </p>
      )}
    </div>
  );
};

// Integrity / liveness signals captured during the interview — for the recruiter's awareness.
const SessionNotes = ({ integrity }) => {
  const i = integrity || {};
  const notes = [];
  if (i.rejoins > 0) notes.push({ icon: '↻', text: `Rejoined ${i.rejoins} time${i.rejoins > 1 ? 's' : ''} (left & returned)`, tone: 'sky' });
  if (i.silence_checkins > 0) notes.push({ icon: '🔔', text: `Prompted ${i.silence_checkins} time${i.silence_checkins > 1 ? 's' : ''} after going quiet`, tone: 'amber' });
  if (i.pasted_answers > 0) notes.push({ icon: '📋', text: `${i.pasted_answers} answer${i.pasted_answers > 1 ? 's were' : ' was'} pasted, not typed`, tone: 'rose' });
  if (notes.length === 0) return null;
  const toneCls = {
    sky: 'bg-sky-50 text-sky-700 dark:bg-sky-900/20 dark:text-sky-300',
    amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300',
    rose: 'bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300',
  };
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm">
      <div className="mb-2 font-semibold text-slate-700 dark:text-slate-200">Session notes</div>
      <div className="flex flex-wrap gap-2">
        {notes.map((n, idx) => (
          <span key={idx} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${toneCls[n.tone]}`}>
            <span aria-hidden="true">{n.icon}</span>{n.text}
          </span>
        ))}
      </div>
    </div>
  );
};

const ReasonList = ({ title, items, tone }) => {
  if (!items || items.length === 0) return null;
  const dot = tone === 'emerald' ? 'text-emerald-500' : 'text-red-500';
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5">{title}</h4>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-slate-600 dark:text-slate-300 flex gap-2">
            <span className={dot}>•</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const READINESS = {
  ready: { label: 'Interview-ready', cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300' },
  almost_ready: { label: 'Almost there', cls: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300' },
  needs_work: { label: 'Keep practicing', cls: 'bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300' },
};

const VERDICT_DOT = {
  demonstrated: 'bg-emerald-500',
  partial: 'bg-amber-500',
  needs_improvement: 'bg-rose-500',
};

/** Candidate-facing mock interview feedback (skills to improve = the key deliverable). */
const MockFeedback = ({ acknowledgement, skillScores, attempts, onRestart }) => {
  const ack = acknowledgement || {};
  const stats = (skillScores && skillScores.stats) || {};
  const skills = (skillScores && skillScores.skills) || [];
  const readiness = READINESS[ack.readiness] || READINESS.almost_ready;
  return (
    <div className="mx-auto w-full max-w-lg space-y-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Your mock interview feedback</h2>
        <span className={`mt-2 inline-block rounded-full px-3 py-1 text-xs font-semibold ${readiness.cls}`}>{readiness.label}</span>
      </div>

      {ack.message && (
        <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">{ack.message}</p>
      )}

      {/* Stats strip */}
      <div className="flex flex-wrap gap-3 text-center">
        {stats.overall_score != null && (
          <div className="flex-1 rounded-xl bg-slate-50 p-3 dark:bg-gray-800">
            <div className="text-lg font-bold text-slate-800 dark:text-white">{stats.overall_score}<span className="text-xs text-slate-400">/100</span></div>
            <div className="text-[11px] text-slate-400">Overall</div>
          </div>
        )}
        {stats.questions_asked != null && (
          <div className="flex-1 rounded-xl bg-slate-50 p-3 dark:bg-gray-800">
            <div className="text-lg font-bold text-slate-800 dark:text-white">{stats.questions_asked}</div>
            <div className="text-[11px] text-slate-400">Questions</div>
          </div>
        )}
        {stats.duration_min ? (
          <div className="flex-1 rounded-xl bg-slate-50 p-3 dark:bg-gray-800">
            <div className="text-lg font-bold text-slate-800 dark:text-white">{stats.duration_min}<span className="text-xs text-slate-400">m</span></div>
            <div className="text-[11px] text-slate-400">Duration</div>
          </div>
        ) : null}
      </div>

      {(ack.strengths || []).length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">What you did well</h3>
          <ul className="mt-1.5 space-y-1">
            {ack.strengths.map((s, i) => (
              <li key={i} className="flex gap-2 text-sm text-slate-600 dark:text-slate-300"><span className="text-emerald-500">✓</span><span>{s}</span></li>
            ))}
          </ul>
        </div>
      )}

      {(ack.skills_to_improve || []).length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">Skills to improve</h3>
          <ul className="mt-1.5 space-y-1">
            {ack.skills_to_improve.map((s, i) => (
              <li key={i} className="flex gap-2 text-sm text-slate-600 dark:text-slate-300"><span className="text-amber-500">→</span><span>{s}</span></li>
            ))}
          </ul>
        </div>
      )}

      {skills.length > 0 && (
        <div>
          <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Skill breakdown</h3>
          <div className="space-y-2">
            {skills.map((s, i) => (
              <div key={i} className="rounded-lg border border-gray-200 p-2.5 dark:border-gray-700">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 font-medium text-slate-700 dark:text-slate-200">
                    <span className={`h-2 w-2 rounded-full ${VERDICT_DOT[s.verdict] || 'bg-slate-400'}`} />{s.skill}
                  </span>
                  {s.score != null && <span className="tabular-nums text-slate-500 dark:text-slate-400">{s.score}/10</span>}
                </div>
                {s.evidence && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{s.evidence}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {(attempts || []).length > 1 && (
        <div>
          <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Your attempts for this job</h3>
          <div className="flex flex-wrap gap-2">
            {attempts.map((a) => (
              <span key={a.interview_id} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600 dark:bg-gray-800 dark:text-slate-300">
                #{a.attempt_no}{a.overall_score != null ? ` · ${a.overall_score}/100` : ''}
              </span>
            ))}
          </div>
        </div>
      )}

      {onRestart && (
        <Button variant="secondary" size="lg" fullWidth onClick={onRestart}>Done</Button>
      )}
    </div>
  );
};

/** Result screen — scorecard + a properly formatted transcript. */
const AIInterviewCompletion = ({ transcript, meta, evaluation, evaluating, onRecalculate, onRestart, candidateView = false, mock = false, acknowledgement = null, skillScores = null, attempts = [] }) => {
  if (candidateView) {
    // Job-seeker mock: show the candidate their skill feedback. (HR candidates see only a thank-you.)
    if (mock && (acknowledgement || skillScores)) {
      return <MockFeedback acknowledgement={acknowledgement} skillScores={skillScores} attempts={attempts} onRestart={onRestart} />;
    }
    return (
      <div className="mx-auto w-full max-w-lg rounded-lg border border-slate-200 bg-white px-6 py-10 text-center shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-300">
          <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="mt-4 text-xl font-semibold text-slate-900 dark:text-white">Thank you for your time</h2>
        <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-300">
          Your interview has been submitted successfully. The hiring team will review it and contact you about the next steps.
        </p>
        <p className="mt-5 text-xs text-slate-400">You can safely close this window.</p>
      </div>
    );
  }

  const turns = parseTranscript(transcript);
  const subtitleParts = [
    meta?.candidate_name,
    meta?.role_title,
    meta?.questions_asked != null ? `${meta.questions_asked} questions` : null,
  ].filter(Boolean);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-8 space-y-6">
      <div className="text-center">
        <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center"
          style={{ backgroundColor: 'var(--color-secondary, #28ACA8)', color: '#fff' }}>
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-slate-800 dark:text-white mt-3">Interview result</h2>
        {subtitleParts.length > 0 && (
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{subtitleParts.join(' · ')}</p>
        )}
      </div>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Evaluation</h3>
          {onRecalculate && evaluation && (
            <button
              type="button"
              onClick={onRecalculate}
              disabled={evaluating}
              className="text-xs px-3 py-1 rounded-full border border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-300 dark:hover:bg-blue-900/20 disabled:opacity-50 transition"
              title="Re-score this interview using the current scoring config"
            >
              {evaluating ? 'Recalculating…' : '↻ Recalculate'}
            </button>
          )}
        </div>
        <Scorecard evaluation={evaluation} evaluating={evaluating} />
        {meta?.integrity && (
          <div className="mt-4">
            <SessionNotes integrity={meta.integrity} />
          </div>
        )}
      </section>

      <section>
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Transcript</h3>
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-4 max-h-[45vh] overflow-y-auto space-y-3">
          {turns.length === 0 && <p className="text-sm text-slate-400">No transcript found.</p>}
          {turns.map((t, i) => (
            <div key={i} className={t.role === 'candidate' ? 'flex justify-end' : 'flex justify-start'}>
              <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm ${
                t.role === 'candidate'
                  ? 'bg-blue-500 text-white rounded-br-sm'
                  : 'bg-white dark:bg-gray-700 text-slate-700 dark:text-slate-100 border border-gray-100 dark:border-gray-600 rounded-bl-sm'
              }`}>
                <div className="text-[10px] uppercase tracking-wide opacity-60 mb-0.5">
                  {t.role === 'candidate' ? 'Candidate' : 'Wiza'}
                </div>
                {t.text}
              </div>
            </div>
          ))}
        </div>
      </section>

      <Button variant="secondary" size="lg" fullWidth onClick={onRestart}>
        Start a new interview
      </Button>
    </div>
  );
};

export default AIInterviewCompletion;
